import React, { useState, createRef, useRef } from "react";
import Dropzone, { DropzoneRef } from "react-dropzone";

import {
  Box,
  Stack,
  Typography,
  ButtonGroup,
  Button,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { UploadFile } from "@mui/icons-material";

interface ImageDropZoneProps {
  documentId?: number;
  value?: string | File;
  label: string;
  onChange: (file: File | null, lang?: string) => void;
  warn?: boolean;
  i18n?: boolean;
  isDetail?: boolean;
  onUpload?: () => void;
  onDelete?: (documentId: number) => void;
  typeText?: string;
  actionButton?: {
    text: string;
    color: "primary" | "secondary" | "info" | "success" | "warning" | "error";
    onClick: () => void;
  };
}

export default function ImageDropZone({
  documentId,
  value,
  label,
  onChange,
  warn,
  i18n,
  isDetail,
  onUpload,
  onDelete,
  typeText,
  actionButton,
}: ImageDropZoneProps) {
  const [selectedLang, setSelectedLang] = useState("tr");
  const dropzoneRef = useRef<DropzoneRef>();
  const openDialog = () => {
    // Note that the ref is set async,
    // so it might be null at some point
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };
  if (i18n) {
    value = value[selectedLang];
  }

  const isNewImage = value && typeof value !== "string";

  return (
    <Box>
      <Dropzone
        accept={{
          "image/*": [],
        }}
        multiple={false}
        ref={dropzoneRef}
        noClick={!isDetail && value ? true : false}
        onDrop={(acceptedFiles) => {
          if (acceptedFiles.length === 0) return;

          if (i18n) {
            onChange(acceptedFiles[0], selectedLang);
          } else {
            onChange(acceptedFiles[0]);
          }
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <section
            style={{
              border: "1px solid",
              borderColor: warn ? "#f44336" : "#c4c4c4",
              padding: "10px",
              borderRadius: "10px",
              borderStyle: "dashed",
              minHeight: "150px",
            }}
          >
            {i18n && (
              <FormControl
                sx={{ m: 1, minWidth: 50, ml: 0 }}
                size="small"
                variant="standard"
              >
                <Select
                  labelId="select-lang-label"
                  id="select-lang"
                  value={selectedLang}
                  onChange={(e) => setSelectedLang(e.target.value)}
                >
                  <MenuItem value={"tr"}>TR</MenuItem>
                  <MenuItem value={"en"}>EN</MenuItem>
                </Select>
              </FormControl>
            )}
            <div
              {...getRootProps()}
              style={{ minHeight: "150px" }}
            >
              <input {...getInputProps()} />
              <Typography variant={"subtitle2"}>{label}</Typography>
              {!value && (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    color: "gray",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "130px",
                    cursor: "pointer",
                  }}
                >
                  <UploadFile style={{ width: "30px", height: "30px" }} />
                  <Typography fontSize={14}>Click to select image</Typography>
                </div>
              )}
              {value && (
                <Box display={"grid"}>
                  <b style={{ fontSize: 14, marginBottom: "2%" }}>{typeText}</b>
                  {value ? (
                    <img
                      src={typeof value === "string" ? value : URL.createObjectURL(value)}
                      width={"100%"}
                      height={"auto"}
                      style={{ objectFit: "contain", background: "#ccc", padding: "10px", maxHeight: "200px" }}
                    />
                  ) : (
                    <>
                      <i className="fa fa-image" />
                    </>
                  )}

                  <Stack
                    direction="row"
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    spacing={2}
                    mt={1}
                    width={"100%"}
                  >
                    <Typography
                      style={{ maxWidth: "89%", overflow: "hidden", textOverflow: "ellipsis" }}
                      variant={"subtitle2"}
                    >
                      {/* @ts-ignore */}
                      {value?.name}
                    </Typography>
                    <ButtonGroup
                      disableElevation
                      variant="contained"
                      aria-label="Disabled elevation buttons"
                    >
                      {actionButton && (
                        <Button
                          color={actionButton.color}
                          onClick={actionButton.onClick}
                        >
                          {actionButton.text}
                        </Button>
                      )}
                      {isNewImage && onUpload && (
                        <Button
                          color="info"
                          onClickCapture={onUpload}
                        >
                          Upload
                        </Button>
                      )}
                      {!isNewImage && !documentId && (
                        <Button
                          color="info"
                          disabled={typeof value === "string"}
                          onClickCapture={(e) => {
                            e.stopPropagation();
                            openDialog();
                          }}
                        >
                          Update
                        </Button>
                      )}
                      <Button
                        color="error"
                        onClickCapture={(e) => {
                          e.stopPropagation();
                          if (documentId) {
                            onDelete(documentId);
                          } else if (i18n) {
                            onChange(null, selectedLang);
                          } else {
                            onChange(null);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </Stack>
                </Box>
              )}
            </div>
          </section>
        )}
      </Dropzone>
      {warn && (
        <Typography
          variant="caption"
          color="error"
        >
          This field is mandatory
        </Typography>
      )}
    </Box>
  );
}
